/**
 * Program-days hook — STUB while the backend ships /me/programs/:id detail.
 *
 * `GET /me/programs` exposes only the shallow program list — name, description,
 * cover, duration_days, certification_type, status — no days[] tree. Until the
 * backend ships a per-program detail endpoint, this hook short-circuits with
 * mock days/courses so the days grid + day-detail screens can render.
 *
 * Reuses the admin `ProgramDayWithCourses` interface (from types/program.ts)
 * so the eventual swap is one-line — the real endpoint is expected to return
 * the same shape.
 */

import { useQuery } from '@tanstack/react-query';
import type { ProgramDayWithCourses } from '../../types/program';
import api from '../../lib/api';

// Toggle to false once /me/programs/:id is wired.
const USE_STUB = false;

export const programDaysKeys = {
  all: (programId: string) => ['me', 'program', programId, 'days'] as const,
};

/** The journey as the learner experiences it: the program's own state plus its
 *  ordered days. A course inside a program is a chapter, so the player needs
 *  both to know whether to hand over the next one or close the program
 *  (Aleix, 2026-09-10). */
export interface ProgramJourney {
  program: {
    id: string;
    name: string;
    completed?: boolean;
    certification_type?: 'none' | 'auto_generated' | 'custom_upload' | null;
  };
  days: ProgramDayWithCourses[];
}

function buildStubDays(programId: string, durationDays: number = 3): ProgramDayWithCourses[] {
  // First day open for the cosmetic state; rest are locked. Real progress
  // states arrive once the backend ships the endpoint.
  const days: ProgramDayWithCourses[] = [];
  const stamp = new Date().toISOString();
  for (let i = 1; i <= durationDays; i++) {
    days.push({
      id: `stub_day_${programId}_${i}`,
      program_id: programId,
      day_number: i,
      title: `Day ${i}`,
      header_image: null,
      courses_count: i === 1 ? 2 : 0,
      created_at: stamp,
      updated_at: stamp,
      courses:
        i === 1
          ? [
              {
                id: `stub_dc_${programId}_${i}_1`,
                day_id: `stub_day_${programId}_${i}`,
                course_id: 'stub_course_1',
                order_index: 0,
                course: {
                  id: 'stub_course_1',
                  title: 'Welcome & onboarding',
                  image_url: null,
                  status: 'published',
                  estimated_duration_minutes: 30,
                },
                created_at: stamp,
              },
              {
                id: `stub_dc_${programId}_${i}_2`,
                day_id: `stub_day_${programId}_${i}`,
                course_id: 'stub_course_2',
                order_index: 1,
                course: {
                  id: 'stub_course_2',
                  title: 'Company values & mission',
                  image_url: null,
                  status: 'published',
                  estimated_duration_minutes: 45,
                },
                created_at: stamp,
              },
            ]
          : [],
    });
  }
  return days;
}

async function fetchJourney(
  programId: string,
  durationDays?: number
): Promise<ProgramJourney> {
  if (USE_STUB) {
    return {
      program: { id: programId, name: '', completed: false, certification_type: 'none' },
      days: buildStubDays(programId, durationDays ?? 3),
    };
  }
  const res = await api.get<{ data: ProgramJourney }>(`/me/programs/${programId}`);
  return res.data.data;
}

/** The whole journey — program state and its days. */
export function useProgramJourney(programId: string | undefined, durationDays?: number) {
  return useQuery({
    queryKey: programId ? programDaysKeys.all(programId) : ['me', 'program', '__none__', 'days'],
    queryFn: () => fetchJourney(programId!, durationDays),
    enabled: !!programId,
  });
}

/** Just the days — same cache entry as the journey above. */
export function useProgramDays(programId: string | undefined, durationDays?: number) {
  return useQuery({
    queryKey: programId ? programDaysKeys.all(programId) : ['me', 'program', '__none__', 'days'],
    queryFn: () => fetchJourney(programId!, durationDays),
    enabled: !!programId,
    select: (journey: ProgramJourney) => journey.days,
  });
}

export const PROGRAM_DAYS_API_GAP = USE_STUB;
